import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TextField,
  MenuItem,
  Button,
  Box,
  Grid,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcCallIcon from '@mui/icons-material/AddIcCall';
import CallEndIcon from '@mui/icons-material/CallEnd';
import {
  useDevices,
  useDaily,
  useDailyEvent,
  DailyVideo,
  useLocalSessionId,
  useParticipantProperty,
} from '@daily-co/daily-react';
import UserMediaError from '../UserMediaError/UserMediaError';
import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";
import { useChatSessionsContext } from "../../lib/contexts/ChatSessionsContext";
import { useCustomThemeContext } from "../../lib/contexts/ThemeContext";
import { Persona, getPersonas } from "../../lib/api/Persona";
import type { Message } from '../../lib/interfaces/Chat';
import { useChat } from '../../lib/hooks/UseChat';
import { stripExtraTags } from '@common/domain/tag-filter';
import { getPersonaBaseName, parsePersonaVariantName } from '../../lib/helpers/personaVariants';
import { MarkdownContent } from '../Chat/MarkdownContent';

import './HairCheck.css';

const PREFERRED_MIC_KEY = 'preferredMicId';
const PREFERRED_CAMERA_KEY = 'preferredCameraId';
const PREFERRED_SPEAKER_KEY = 'preferredSpeakerId';

interface HairCheckProps {
  joinCall: (username: string, url?: string) => void;
  cancelCall: (e: React.MouseEvent | React.FormEvent) => void;
  persona: Persona;
  createCall: (persona: Persona, chatId?: string, conversationalContext?: Array<{ role: 'user' | 'assistant'; content: string }>) => Promise<string>;
  isCreating: boolean;
  onTeachingInteraction?: () => void;
  personaSelectionMode?: 'selectable' | 'locked';
  continuationChatId?: string;
}

const HISTORY_LOAD_BUDGET_MS = 10_000;

function withHistoryTimeout<T>(request: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error('Conversation history took too long to load.')),
      HISTORY_LOAD_BUDGET_MS,
    );
    request.then((value) => {
      window.clearTimeout(timer);
      resolve(value);
    }).catch((error) => {
      window.clearTimeout(timer);
      reject(error);
    });
  });
}

export default function HairCheck({
  joinCall,
  cancelCall,
  persona,
  createCall,
  isCreating,
  onTeachingInteraction,
  personaSelectionMode = 'selectable',
  continuationChatId,
}: HairCheckProps) {
  const localSessionId = useLocalSessionId();
  const initialUsername = useParticipantProperty(localSessionId, 'user_name') as string;
  const {
    currentCam,
    currentMic,
    currentSpeaker,
    microphones,
    speakers,
    cameras,
    setMicrophone,
    setCamera,
    setSpeaker,
  } = useDevices();
  const callObject = useDaily();
  const { getChatMessages } = useChat();

  const [getUserMediaError, setGetUserMediaError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyLoadError, setHistoryLoadError] = useState<string | null>(null);
  const [historyLoadAttempt, setHistoryLoadAttempt] = useState(0);
  const { userInfo, updateUserInfo } = useUserInfoContext();
  const [username, setUsername] = useState(initialUsername || userInfo?.name || '');
  const {
    selectedChatSession,
    messages,
    setMessages,
    updatePersona: setContextPersona,
  } = useChatSessionsContext();
  const { isDarkMode } = useCustomThemeContext();

  // Persona variant selection (e.g. Caesar v1 vs Caesar v2)
  const [allPersonas, setAllPersonas] = useState<Persona[]>([]);
  const [activePersonaId, setActivePersonaId] = useState<string>(persona?.id ?? '');

  const basePersonaName = useMemo(() => getPersonaBaseName(persona?.name), [persona?.name]);

  const personaVariants = useMemo(() => {
    if (personaSelectionMode === 'locked') return [persona];
    const source = allPersonas.length > 0 ? allPersonas : [persona];
    const variants = source.filter((p) => getPersonaBaseName(p?.name) === basePersonaName);
    // If we only have the current persona available, keep it as a single-variant list.
    const safe = variants.length > 0 ? variants : [persona];
    return [...safe].sort((a, b) => {
      const av = parsePersonaVariantName(a?.name).version ?? -1;
      const bv = parsePersonaVariantName(b?.name).version ?? -1;
      return bv - av;
    });
  }, [allPersonas, persona, basePersonaName, personaSelectionMode]);

  const activePersona = useMemo(() => {
    if (personaSelectionMode === 'locked') return persona;
    return personaVariants.find((p) => p.id === activePersonaId) ?? personaVariants[0] ?? persona;
  }, [personaVariants, activePersonaId, persona, personaSelectionMode]);

  // Conversation history state
  const activeChatId = continuationChatId
    ?? (personaSelectionMode === 'selectable' ? selectedChatSession ?? undefined : undefined);
  const [historyLoading, setHistoryLoading] = useState(Boolean(activeChatId));
  const [continuationMessages, setContinuationMessages] = useState<Message[]>([]);
  const activeMessages = continuationChatId ? continuationMessages : messages;

  const startCallLabel =
    activeChatId ? 'Continue Conversation' : 'Start Call';

  useEffect(() => {
    setUsername(initialUsername);
  }, [initialUsername]);

  useEffect(() => {
    // Keep active persona in sync when the prop changes (e.g. user picked a different card)
    setActivePersonaId(persona?.id ?? '');
  }, [persona?.id]);

  useEffect(() => {
    if (!userInfo || personaSelectionMode === 'locked') return;
    let mounted = true;
    getPersonas(userInfo)
      .then((res) => {
        if (!mounted) return;
        setAllPersonas(Array.isArray(res) ? res : []);
      })
      .catch((err) => {
        console.warn('[HairCheck] Failed to fetch personas for variants:', err);
      });
    return () => {
      mounted = false;
    };
  }, [userInfo, personaSelectionMode]);

  useEffect(() => {
    if (!activeChatId) {
      setContinuationMessages([]);
      setHistoryLoading(false);
      setHistoryLoadError(null);
      if (!activeChatId && personaSelectionMode === 'selectable') setMessages([]);
      return;
    }
    if (!userInfo) {
      setHistoryLoading(false);
      return;
    }

    let isCurrent = true;
    if (continuationChatId) setContinuationMessages([]);
    setHistoryLoadError(null);
    setHistoryLoading(true);

    withHistoryTimeout(getChatMessages(userInfo, activeChatId))
      .then(msgs => {
        if (isCurrent && msgs) {
          if (continuationChatId) setContinuationMessages(msgs);
          else setMessages(msgs);
        }
      })
      .catch(err => {
        if (isCurrent) {
          console.error("Failed to load messages", err);
          setHistoryLoadError('Conversation history could not be loaded. Retry before continuing.');
        }
      })
      .finally(() => {
        if (isCurrent) setHistoryLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [
    userInfo,
    activeChatId,
    continuationChatId,
    getChatMessages,
    setMessages,
    personaSelectionMode,
    historyLoadAttempt,
  ]);

  useDailyEvent(
    'camera-error',
    useCallback(() => {
      setGetUserMediaError(true);
    }, []),
  );

  // Restore preferred devices
  useEffect(() => {
    if (microphones.length > 0) {
      const savedMicId = localStorage.getItem(PREFERRED_MIC_KEY);
      if (savedMicId && currentMic?.device?.deviceId !== savedMicId) {
        if (microphones.some(m => m.device.deviceId === savedMicId)) {
          setMicrophone(savedMicId);
        }
      }
    }
  }, [microphones, currentMic, setMicrophone]);

  useEffect(() => {
    if (cameras.length > 0) {
      const savedCameraId = localStorage.getItem(PREFERRED_CAMERA_KEY);
      if (savedCameraId && currentCam?.device?.deviceId !== savedCameraId) {
        if (cameras.some(c => c.device.deviceId === savedCameraId)) {
          setCamera(savedCameraId);
        }
      }
    }
  }, [cameras, currentCam, setCamera]);

  useEffect(() => {
    if (speakers.length > 0) {
      const savedSpeakerId = localStorage.getItem(PREFERRED_SPEAKER_KEY);
      if (savedSpeakerId && currentSpeaker?.device?.deviceId !== savedSpeakerId) {
        if (speakers.some(s => s.device.deviceId === savedSpeakerId)) {
          setSpeaker(savedSpeakerId);
        }
      }
    }
  }, [speakers, currentSpeaker, setSpeaker]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    callObject?.setUserName(e.target.value);
  };

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      let chatId: string | undefined = undefined;
      let conversationalContext: Array<{ role: 'user' | 'assistant'; content: string }> | undefined = undefined;

      if (activeChatId) {
        chatId = activeChatId;

        if (activeMessages.length > 0) {
          // Map Message[] to conversationalContext format
          conversationalContext = activeMessages.map(msg => ({
            role: (msg.fromUser ? 'user' : 'assistant') as 'user' | 'assistant',
            content: msg.message
          }));
        }
      }

      if (createCall) {
        const callPersona = personaSelectionMode === 'locked' ? persona : activePersona;
        const url = await createCall(callPersona, chatId, conversationalContext);
        joinCall(username?.trim() || '', url);
      } else {
        joinCall(username?.trim() || '');
      }
    } catch (err: any) {
      console.error("Join call error:", err);
      setError(err.message || "Failed to initialize conversation. Please try again.");
    }
  };

  const updateMicrophone = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMicrophone(e.target.value);
    localStorage.setItem(PREFERRED_MIC_KEY, e.target.value);
  };

  const updateSpeakers = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSpeaker(e.target.value);
    localStorage.setItem(PREFERRED_SPEAKER_KEY, e.target.value);
  };

  const updateCamera = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCamera(e.target.value);
    localStorage.setItem(PREFERRED_CAMERA_KEY, e.target.value);
  };

  const handleCancelCall = (e: React.MouseEvent) => {
    if (userInfo) {
      updateUserInfo({
        ...userInfo,
        isInCall: false,
      });
    }
    cancelCall(e);
  };

  if (!persona) {
    return <div>Loading...</div>;
  }

  return getUserMediaError ? (
    <UserMediaError />
  ) : (
    <Box
      className={`hair-check-container ${isDarkMode ? 'dark' : 'light'}`}
      onKeyDown={onTeachingInteraction}
      onPointerDown={onTeachingInteraction}
      onWheel={onTeachingInteraction}
    >
      <Grid container spacing={4} className="hair-check-grid">
        {/* LEFT COLUMN */}
        <Grid item xs={12} md={6} className="left-col">
          <Box className="left-panel">
            {/* Profile (avatar + name + title) */}
            <Box className="profile-block">
              <Avatar
                alt={activePersona.name}
                src={activePersona.imageURL || activePersona.avatar_url}
                className="persona-avatar"
                sx={{
                  width: 160,
                  height: 160,
                  margin: '0 auto var(--space-md)',
                  border: '4px solid var(--color-accent-soft)',
                  '& .MuiAvatar-img': {
                    objectFit: 'cover'
                  }
                }}
              />

              <Typography variant="h5" className="persona-name">
                {activePersona.name}
              </Typography>

              <Typography variant="body2" className="persona-title">
                {activePersona.title}
              </Typography>

              {personaSelectionMode === 'selectable' && personaVariants.length > 1 && (
                <Box sx={{ mt: 2, width: '100%' }}>
                  <TextField
                    select
                    fullWidth
                    label="Version"
                    value={activePersonaId}
                    onChange={(e) => {
                      const nextId = e.target.value as string;
                      setActivePersonaId(nextId);
                      const nextPersona = personaVariants.find(p => p.id === nextId);
                      if (nextPersona) {
                        setContextPersona(nextPersona);
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'var(--glass-white-subtle)',
                      }
                    }}
                  >
                    {personaVariants.map((p) => {
                      const parsed = parsePersonaVariantName(p?.name);
                      const label = parsed.version ? `v${parsed.version}` : (p?.name ?? 'Default');
                      return (
                        <MenuItem key={p.id} value={p.id}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Box>
              )}
            </Box>

            {personaSelectionMode === 'selectable' && (
              <>
                {/* Conversation Transcript */}
                <Typography className="history-heading">
                  {selectedChatSession ? 'Conversation Transcript' : 'New Conversation'}
                </Typography>

                <Box className="conversation-history-box">
                  {historyLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : messages.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {messages.map((msg, idx) => (
                        <Box
                          key={`${msg.exchangeId}-${idx}`}
                          className="transcript-bubble"
                          sx={{
                            p: 1.5,
                            borderRadius: 'var(--radius-md)',
                            background: msg.fromUser ? 'var(--color-sage-light)' : 'var(--glass-white-strong)',
                            border: '1px solid var(--glass-border-subtle)',
                            alignSelf: msg.fromUser ? 'flex-end' : 'flex-start',
                            maxWidth: '90%',
                            boxShadow: 'var(--shadow-sm)',
                          }}
                        >
                          <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 600, mb: 0.5, display: 'block' }}>
                            {msg.fromUser ? 'You' : activePersona.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'var(--color-text)', lineHeight: 1.5 }}>
                            <MarkdownContent content={stripExtraTags(msg.message)} />
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', textAlign: 'center', py: 4 }}>
                      {selectedChatSession
                        ? 'No messages in this session.'
                        : 'Select a previous conversation from the sidebar or start a fresh one.'}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid item xs={12} md={6} className="right-col">
          <Box className="right-panel">
            <Box component="form" className="hair-check" onSubmit={handleJoin}>
              <Typography variant="h4" className="right-title">
                Ready to join?
              </Typography>
              <Typography variant="body2" className="right-subtitle">
                Check your audio and video before starting.
              </Typography>

              <Box className="video-preview">
                {localSessionId && <DailyVideo sessionId={localSessionId} mirror type="video" />}
              </Box>

              <TextField
                id="name"
                label="Your Name"
                variant="outlined"
                onChange={handleChange}
                value={username ?? ''}
                fullWidth
                margin="normal"
                sx={{
                   '& .MuiOutlinedInput-root': {
                      backgroundColor: 'var(--glass-white-subtle)',
                      mb: "16px",
                   }
                }}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    id="cameraSelect"
                    select
                    label="Camera"
                    onChange={updateCamera}
                    value={currentCam?.device?.deviceId ?? ''}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                          backgroundColor: 'var(--glass-white-subtle)',
                      }
                    }}
                  >
                    {cameras.map((camera) => (
                      <MenuItem key={`camera-${camera.device.deviceId}`} value={camera.device.deviceId}>
                        {camera.device.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    id="micSelect"
                    select
                    label="Microphone"
                    onChange={updateMicrophone}
                    value={currentMic?.device?.deviceId ?? ''}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                          backgroundColor: 'var(--glass-white-subtle)',
                      }
                    }}
                  >
                    {microphones.map((mic) => (
                      <MenuItem key={`mic-${mic.device.deviceId}`} value={mic.device.deviceId}>
                        {mic.device.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <TextField
                id="speakersSelect"
                select
                label="Speakers"
                onChange={updateSpeakers}
                value={currentSpeaker?.device?.deviceId ?? ''}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                      backgroundColor: 'var(--glass-white-subtle)',
                  }
                }}
              >
                {speakers.map((speaker) => (
                  <MenuItem key={`speaker-${speaker.device.deviceId}`} value={speaker.device.deviceId}>
                    {speaker.device.label}
                  </MenuItem>
                ))}
              </TextField>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {historyLoadError && (
                <Alert
                  severity="error"
                  action={<Button color="inherit" onClick={() => setHistoryLoadAttempt((attempt) => attempt + 1)}>Retry</Button>}
                  sx={{ mt: 2 }}
                >
                  {historyLoadError}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isCreating || historyLoading || Boolean(historyLoadError)}
                startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : <AddIcCallIcon />}
                sx={{
                  mt: 3,
                  py: 1.5,
                  backgroundColor: 'var(--color-accent)',
                  '&:hover': { backgroundColor: 'var(--color-accent-dark, #a67c00)' }
                }}
              >
                {isCreating ? 'Starting...' : startCallLabel}
              </Button>

              <Button
                variant="outlined"
                fullWidth
                onClick={handleCancelCall}
                sx={{
                  mt: 1.5,
                  borderColor: 'var(--color-text-secondary)',
                  color: 'var(--color-text-secondary)',
                  '&:hover': { borderColor: 'var(--color-text)', color: 'var(--color-text)' }
                }}
                startIcon={<CallEndIcon />}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

    </Box>
  );
}
